<style scoped>
.mc-thumb {
	width: 64px;
	height: 48px;
	flex-shrink: 0;
	overflow: hidden;
	border-radius: 0.4rem;
}
.mc-thumb img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}
/* Photos saved in an earlier session have no local preview (RMO has no public
   image route), so a placeholder tile stands in for the thumbnail. */
.mc-thumb-saved {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #e9f2fb;
	color: #0d6efd;
}
.mc-thumb-remove {
	position: absolute;
	top: -0.35rem;
	right: -0.35rem;
	padding: 0 0.2rem;
	line-height: 1.1;
	border-radius: 50%;
}
.larvae-btn.active-yes {
	background-color: #198754;
	border-color: #198754;
	color: #fff;
}
.larvae-btn.active-no {
	background-color: #dc3545;
	border-color: #dc3545;
	color: #fff;
}
.larvae-btn.active-unsure {
	background-color: #6c757d;
	border-color: #6c757d;
	color: #fff;
}
.mc-video-overlay {
	position: fixed;
	inset: 0;
	background-color: rgba(0, 0, 0, 0.55);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 3000;
	padding: 1rem;
}
.mc-video-modal {
	width: 100%;
	max-width: min(30rem, calc(100vw / var(--mc-scale, 1)));
	overflow: hidden;
}
.mc-video-frame {
	position: relative;
	aspect-ratio: 16 / 9;
	width: 100%;
}
.mc-video-frame video {
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	border: 0;
	background-color: #000;
}
.mc-video-attribution {
	line-height: 1.3;
}
</style>
<template>
	<div>
		<!-- Unmarked -->
		<div v-if="!obs" class="d-grid gap-1">
			<button type="button" class="btn btn-primary" @click="have">
				{{ t("i_have_this") }}
			</button>
			<button type="button" class="btn btn-outline-secondary" @click="notHave">
				{{ t("i_dont_have_this") }}
			</button>
		</div>

		<!-- Marked as not present -->
		<div
			v-else-if="!obs.present"
			class="d-flex align-items-center justify-content-between gap-2 flex-wrap"
		>
			<span class="small text-success">
				<i class="bi bi-check-circle me-1"></i>{{ t("marked_not_present") }}
			</span>
			<button
				type="button"
				class="btn btn-sm btn-outline-secondary"
				@click="removeObservation"
			>
				{{ t("undo") }}
			</button>
		</div>

		<!-- Present: detail tools -->
		<div v-else class="d-flex flex-column gap-1">
			<div class="d-flex align-items-start gap-2 flex-wrap">
				<div v-if="photos.length > 0" class="d-flex gap-1 flex-wrap">
					<div
						v-for="photo in photos"
						:key="photo.id"
						class="mc-thumb position-relative"
					>
						<img
							v-if="store.previews.get(photo.id)"
							:src="store.previews.get(photo.id)"
							:alt="t('photo_alt')"
						/>
						<span v-else class="mc-thumb-saved" :title="t('photo_saved')">
							<i class="bi bi-image"></i>
						</span>
						<button
							type="button"
							class="btn btn-sm btn-danger mc-thumb-remove"
							:aria-label="t('remove_photo')"
							:title="t('remove_photo')"
							@click="removePhoto(photo.id)"
						>
							<i class="bi bi-x"></i>
						</button>
					</div>
				</div>
				<input
					ref="fileInput"
					type="file"
					accept="image/*"
					capture="environment"
					multiple
					class="d-none"
					@change="onFile"
				/>
				<button
					type="button"
					class="btn btn-sm btn-outline-primary"
					@click="pickFile"
				>
					<i class="bi bi-camera me-1"></i>{{ t("add_photo") }}
				</button>
				<button
					type="button"
					class="btn btn-sm btn-outline-danger ms-auto"
					@click="removeObservation"
				>
					<i class="bi bi-x-circle me-1"></i>{{ t("remove") }}
				</button>
			</div>

			<div>
				<span class="d-block small fw-semibold mb-1">
					{{ t("larvae_present") }}
				</span>
				<div class="btn-group btn-group-sm d-flex w-100" role="group">
					<button
						type="button"
						class="btn btn-outline-secondary larvae-btn"
						:class="{ 'active-yes': larvaeYes }"
						@click="setLarvae(true)"
					>
						{{ t("yes") }}
					</button>
					<button
						type="button"
						class="btn btn-outline-secondary larvae-btn"
						:class="{ 'active-no': larvaeNo }"
						@click="setLarvae(false)"
					>
						{{ t("no") }}
					</button>
					<button
						type="button"
						class="btn btn-outline-secondary larvae-btn"
						:class="{ 'active-unsure': notSure }"
						@click="setLarvae(null)"
					>
						{{ t("not_sure") }}
					</button>
				</div>
				<div
					v-if="notSure"
					class="small mt-1 d-flex align-items-center justify-content-between gap-2 flex-wrap"
				>
					<span>{{ t("larvae_video_hint") }}</span>
					<button
						type="button"
						class="btn btn-sm btn-outline-primary"
						@click="showVideo = true"
					>
						<i class="bi bi-play-circle me-1"></i>{{ t("larvae_video_btn") }}
					</button>
				</div>
			</div>

			<div class="form-check">
				<input
					:id="'fixed-' + habitat.key"
					class="form-check-input"
					type="checkbox"
					:checked="obs.already_fixed === true"
					@change="onFixed"
				/>
				<label class="form-check-label" :for="'fixed-' + habitat.key">
					{{ t("already_fixed") }}
				</label>
			</div>
		</div>

		<!-- Larvae identification video (Not sure) -->
		<div
			v-if="showVideo"
			class="mc-video-overlay"
			role="dialog"
			aria-modal="true"
			@click.self="showVideo = false"
		>
			<div class="mc-video-modal card">
				<div
					class="card-header d-flex align-items-center justify-content-between"
				>
					<strong class="small">{{ t("larvae_video_title") }}</strong>
					<button
						type="button"
						class="btn-close"
						:aria-label="t('close')"
						@click="showVideo = false"
					></button>
				</div>
				<div class="card-body p-0">
					<div class="mc-video-frame">
						<video
							controls
							playsinline
							preload="metadata"
							loop
							muted
							:poster="LARVAE_VIDEO_POSTER_URL"
						>
							<!-- WebM first: Firefox and Chromium both honour source order. -->
							<source :src="LARVAE_VIDEO_WEBM_URL" type="video/webm" />
							<source :src="LARVAE_VIDEO_URL" type="video/mp4" />
							{{ t("larvae_video_btn") }}
						</video>
					</div>
					<p class="mc-video-attribution small text-muted mb-0 p-2">
						{{ t("larvae_video_attribution") }}
					</p>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import {
	LARVAE_VIDEO_POSTER_URL,
	LARVAE_VIDEO_URL,
	LARVAE_VIDEO_WEBM_URL,
	useT,
} from "@/rmo/content/mosquito/copy";
import { useStoreVirtualInspection } from "@/rmo/store/virtualinspection";
import type { VirtualInspectionHabitat } from "@/type/api";

interface Props {
	habitat: VirtualInspectionHabitat;
	token: string;
}

const props = defineProps<Props>();
const store = useStoreVirtualInspection();
const t = useT();
const fileInput = ref<HTMLInputElement | null>(null);
const showVideo = ref(false);
// "Not sure" has no distinct server value (null == unanswered); track the tap
// locally so the option shows selected during the session.
const notSure = ref(false);

// Reset per-habitat local state when the walk advances to a new card.
watch(
	() => props.habitat.key,
	() => {
		notSure.value = false;
		showVideo.value = false;
	},
);

const obs = computed(
	() =>
		store.state?.observations.find(
			(o) => o.habitat_key === props.habitat.key,
		) ?? null,
);
const photos = computed(() => obs.value?.photos ?? []);
const larvaeYes = computed(() => obs.value?.larvae_present === true);
const larvaeNo = computed(() => obs.value?.larvae_present === false);

function have() {
	notSure.value = false;
	store.saveObservation(props.token, {
		habitat_key: props.habitat.key,
		present: true,
	});
}

function notHave() {
	notSure.value = false;
	store.saveObservation(props.token, {
		habitat_key: props.habitat.key,
		present: false,
	});
}

// Both "Undo" (from marked-not-present) and "Remove" (from present) put the
// card back to truly unmarked — the observation row is deleted so it counts in
// neither Confirmed nor Clear.
function removeObservation() {
	notSure.value = false;
	for (const photo of photos.value) {
		store.clearPreview(photo.id);
	}
	store.deleteObservation(props.token, props.habitat.key);
}

// One photo goes; the habitat and its other photos stay.
function removePhoto(photoId: string) {
	store.removeObservationPhoto(props.token, props.habitat.key, photoId);
}

function setLarvae(value: boolean | null) {
	notSure.value = value === null;
	store.saveObservation(props.token, {
		habitat_key: props.habitat.key,
		present: true,
		larvae_present: value,
	});
}

function onFixed(e: Event) {
	store.saveObservation(props.token, {
		habitat_key: props.habitat.key,
		present: true,
		already_fixed: (e.target as HTMLInputElement).checked,
	});
}

function pickFile() {
	fileInput.value?.click();
}

async function onFile(e: Event) {
	const input = e.target as HTMLInputElement;
	const files = [...(input.files ?? [])];
	if (files.length === 0) {
		return;
	}
	await store.uploadObservationPhoto(props.token, props.habitat.key, files);
	input.value = "";
}
</script>
